/**
 * A sport's scoring: the workflows its fixtures can use, in the same words as
 * /workflows.
 *
 * Two kinds. The sport's own scoring, which can be more than one (a fork made
 * for one format, such as Cricket's Hundred), and the workflows for every
 * sport, such as Results. Each card lists its versions: published, draft or
 * replaced. Nothing changes here; a change in the plugin arrives as a new
 * version.
 *
 * The desks built only for this sport are apps too, so they close the page,
 * with a way to the registry that says which kinds of fixture each one is for.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, TriangleAlert } from "lucide-react";

import { Card, Empty, LinkButton, PageHead, Pill, Problem, Section, Table, Td, Th } from "@/components/kit";
import { ChooseAppsDialog } from "@/components/workflows/ChooseApps";
import { VersionStatePill, WorkflowCard } from "@/components/workflows/WorkflowCard";
import styles from "@/components/workflows/workflows.module.css";
import {
  appKindLabel,
  baseWorkflow,
  listApps,
  listIntegrationBriefs,
  listWorkflowDetails,
  roleDoing,
  versionWords,
  type WorkflowDetail,
} from "@/lib/api/workflows";
import { displayName } from "@/lib/format";
import { listPrograms, type ProgramRow } from "@/lib/scoring/api";
import { sharedVersions } from "@/lib/scoring/versions";
import { useApi } from "@/spa/useApi";

type Entry = { row: WorkflowDetail; versions: ProgramRow[] | null };

export function ScoringProgramsPage() {
  const { sport = "" } = useParams<{ sport: string }>();
  const sportName = displayName(sport);

  // A version list that fails to load says so on its card; the cards still show.
  const loaded = useApi(
    async (): Promise<Entry[]> => {
      const [rows, mine] = await Promise.all([
        listWorkflowDetails({ sport }),
        listPrograms({ sport }).catch(() => null),
      ]);
      const lists = await Promise.all(
        rows.map((row) =>
          row.ownerKind === "sport"
            ? Promise.resolve(
                mine && mine.filter((version) => (version.workflow ?? version.sportCode) === row.code),
              )
            : listPrograms({ workflow: row.code }).then(
                (found) => sharedVersions(found, row.code),
                () => null,
              ),
        ),
      );
      return rows.map((row, index) => ({ row, versions: lists[index] }));
    },
    [sport],
    "the admin API is not reachable",
  );
  const apps = useApi(() => listApps(), []);
  const integrations = useApi(() => listIntegrationBriefs(), [], "could not load the integrations");
  const [choosing, setChoosing] = useState<string | null>(null);

  const entries = loaded.data ?? [];
  const rows = entries.map((entry) => entry.row);
  const own = entries.filter((entry) => entry.row.ownerKind === "sport");
  const shared = entries.filter((entry) => entry.row.ownerKind !== "sport");
  const chosen = rows.find((row) => row.code === choosing);
  const desks = (apps.data ?? []).filter((app) => app.sportCode === sport);
  const uisHref = `/${sport}/scoring/uis`;

  const card = ({ row, versions }: Entry) => (
    <WorkflowCard
      key={row.code}
      workflow={row}
      basedOn={baseWorkflow(row, rows)}
      integrations={integrations.data}
      integrationsError={integrations.error}
      onChooseApps={() => setChoosing(row.code)}
    >
      <dt>Versions</dt>
      <dd>
        <VersionList versions={versions} sport={sport} ownedBySport={row.ownerKind === "sport"} />
      </dd>
    </WorkflowCard>
  );

  const loading = (
    <Card>
      <Empty title="Loading…" />
    </Card>
  );

  return (
    <>
      <PageHead
        title="Scoring"
        note={`How ${sportName} fixtures are recorded. Every change goes through a workflow: ${sportName}'s own scoring, or Results, which works for every sport.`}
        actions={<LinkButton to="/workflows">All workflows</LinkButton>}
      />

      {loaded.error && <Problem>Could not load the workflows: {loaded.error}</Problem>}

      <Section
        title={`${sportName}'s own scoring`}
        note="Records a match play by play, in a scoring desk."
      >
        {!loaded.data ? (
          loaded.error ? null : (
            loading
          )
        ) : own.length === 0 ? (
          <Card>
            <Empty
              title={`${sportName} has no scoring of its own`}
              note={`At an event, ${sportName} fixtures use the workflow the event chooses, such as Results below. A sport gets its own scoring from the flows plugin.`}
            />
          </Card>
        ) : (
          <div className={styles.grid}>{own.map(card)}</div>
        )}
      </Section>

      <Section
        title="Results, for every sport"
        note={`Works for a fixture in any sport, ${sportName} too: status, start, venue, sides, scores and the result.`}
      >
        {!loaded.data ? (
          loaded.error ? null : (
            loading
          )
        ) : shared.length === 0 ? (
          <Card>
            <Empty
              title="No workflow for every sport yet"
              note="Workflows come from the flows plugin. They show here once omnium has installed it."
              action={<LinkButton to="/workflows">Open workflows</LinkButton>}
            />
          </Card>
        ) : (
          <div className={styles.grid}>{shared.map(card)}</div>
        )}
      </Section>

      <Section
        title={`Desks for ${sportName}`}
        note={`Scoring desks built only for ${sportName}. A desk is an app: it opens for a workflow once it is ticked under Choose apps. Manage desks to say which kinds of fixture each one is for.`}
        actions={<LinkButton to={uisHref}>Manage {sportName} desks</LinkButton>}
      >
        {apps.error ? (
          <Problem>Could not load the apps: {apps.error}</Problem>
        ) : !apps.data ? (
          loading
        ) : desks.length === 0 ? (
          <Card>
            <Empty
              title={`No desk built only for ${sportName}`}
              note="Apps for every sport, such as the operations console, are listed on the Workflows page."
              action={<LinkButton to={uisHref}>Register a desk</LinkButton>}
            />
          </Card>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Desk</Th>
                <Th>Address</Th>
                <Th>Workflows that open in it</Th>
              </tr>
            </thead>
            <tbody>
              {desks.map((app) => (
                <tr key={app.id}>
                  <Td strong>
                    <div className={styles.appName}>
                      {app.name}
                      <Pill>{appKindLabel(app.kind)}</Pill>
                    </div>
                    <p className={styles.appDesc}>
                      {app.description ? `${app.description} ` : ""}
                      For{" "}
                      {app.appliesTo.length > 0
                        ? `${app.appliesTo.map(displayName).join(", ")} fixtures.`
                        : `every ${sportName} fixture.`}
                    </p>
                  </Td>
                  <Td>
                    <a className={styles.url} href={app.url} target="_blank" rel="noreferrer">
                      {app.url}
                      <ExternalLink size={12} aria-hidden />
                    </a>
                  </Td>
                  <Td>
                    {app.workflows.length === 0 ? (
                      <span className={styles.warnText}>
                        <TriangleAlert size={14} aria-hidden />
                        None yet
                      </span>
                    ) : (
                      <div className={styles.opensList}>
                        {app.workflows.map((workflow) => (
                          <span key={`${workflow.code}/${workflow.role}`}>
                            {workflow.name}{" "}
                            <span className={styles.small}>
                              · {roleDoing(workflow.role)}
                              {workflow.isDefault ? " · default" : ""}
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      {chosen && (
        <ChooseAppsDialog
          workflow={chosen}
          apps={apps.data}
          onClose={() => setChoosing(null)}
          onSaved={() => {
            setChoosing(null);
            loaded.reload();
            apps.reload();
          }}
        />
      )}
    </>
  );
}

function VersionList({
  versions,
  sport,
  ownedBySport,
}: {
  versions: ProgramRow[] | null;
  sport: string;
  ownedBySport: boolean;
}) {
  if (versions === null) return <span className={styles.none}>Could not load the versions.</span>;
  if (versions.length === 0) {
    return (
      <span className={styles.none}>
        No version yet. It appears once omnium has installed the flows plugin.
      </span>
    );
  }
  const sorted = [...versions].sort((a, b) => b.version - a.version);
  return (
    <ul className={styles.versions}>
      {sorted.map((version) => (
        <li key={version.id} className={styles.version}>
          <span className={styles.versionName}>Version {version.version}</span>
          <VersionStatePill status={version.status} />
          {ownedBySport && (
            <span className={styles.quiet}>
              {version.scope.length > 0
                ? `for ${version.scope.map(displayName).join(", ")} fixtures only`
                : `for every ${displayName(sport)} fixture`}
            </span>
          )}
          <span className={styles.quiet}>· {version.commands} commands</span>
          {sorted.length > 1 && (
            <Link to={`/${sport}/scoring/${version.id}`}>Open this version</Link>
          )}
          <span className={styles.versionNote}>{versionWords(version.status).note}</span>
        </li>
      ))}
    </ul>
  );
}
